import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Input,
  Select,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { FaEdit, FaImage, FaPlus, FaTrash } from "react-icons/fa";
import { apiUrl } from "../config";
import {
  TEMPLATE_TYPES,
  resolveTemplateType,
} from "../constants/templateTypes";

// Shared style for rendered markdown. Keeps the admin preview visually
// identical to what a Campaign Home viewer sees on the Templates tab.
const markdownSx = {
  "h1, h2, h3, h4": { fontWeight: "semibold", mt: 3, mb: 2 },
  h1: { fontSize: "xl" },
  h2: { fontSize: "lg" },
  h3: { fontSize: "md" },
  p: { my: 2 },
  "ul, ol": { pl: 6, my: 2 },
  li: { my: 1 },
  a: { color: "green.600", textDecoration: "underline" },
  blockquote: {
    borderLeft: "4px solid",
    borderColor: "gray.200",
    pl: 3,
    color: "gray.600",
    my: 2,
  },
  code: {
    bg: "gray.100",
    px: 1,
    borderRadius: "sm",
    fontFamily: "mono",
    fontSize: "sm",
  },
  "pre code": { display: "block", p: 3, overflowX: "auto" },
  hr: { my: 4, borderColor: "gray.200" },
  img: { maxWidth: "100%", height: "auto", my: 2, borderRadius: "sm" },
  table: { width: "100%", my: 3, borderCollapse: "collapse" },
  "th, td": {
    border: "1px solid",
    borderColor: "gray.200",
    px: 2,
    py: 1,
    textAlign: "left",
  },
};

const authHeader = () => ({
  Authorization: `Bearer ${sessionStorage.getItem("idToken")}`,
});

// Platform-admin CRUD for the `templates` collection. Rendered inside the
// Platform Dashboard's Templates tab. Reads happen once on mount; edits
// update local state after a successful API call so the table refreshes
// without a full re-fetch.
const TemplatesAdmin = () => {
  const toast = useToast();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const editor = useDisclosure();
  const [editing, setEditing] = useState(null); // null = create, obj = edit
  const [form, setForm] = useState({
    title: "",
    category: "",
    type: "",
    contents: "",
    images: [],
    published: true,
  });
  const [togglingId, setTogglingId] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  const onPickImage = () => fileInputRef.current?.click();

  const onImageChosen = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    // Reset so re-picking the same file still fires onChange.
    event.target.value = "";
    try {
      setUploadingImage(true);
      const data = new FormData();
      data.append("image", file);
      const res = await axios.post(
        `${apiUrl}/admin/templates/upload-image`,
        data,
        { headers: authHeader() },
      );
      setForm((f) => ({
        ...f,
        images: [
          ...(f.images || []),
          { url: res.data.url, filename: file.name },
        ],
      }));
      toast({ title: "Image attached", status: "success", duration: 2000 });
    } catch (err) {
      console.error("Image upload failed", err);
      toast({
        title: "Image upload failed",
        description: err?.response?.data?.error || err.message,
        status: "error",
      });
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = (index) => {
    setForm((f) => ({
      ...f,
      images: (f.images || []).filter((_, i) => i !== index),
    }));
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${apiUrl}/templates?includeUnpublished=true`,
          { headers: authHeader() },
        );
        if (!cancelled) setTemplates(res.data.templates || []);
      } catch (err) {
        console.error("Load templates failed", err);
        toast({
          title: "Failed to load templates",
          description: err?.response?.data?.error || err.message,
          status: "error",
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [toast]);

  const sortedTemplates = useMemo(() => {
    return [...templates].sort((a, b) => {
      const c = (a.category || "").localeCompare(b.category || "");
      if (c !== 0) return c;
      return (a.title || "").localeCompare(b.title || "");
    });
  }, [templates]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      title: "",
      category: "",
      type: "",
      contents: "",
      images: [],
      published: true,
    });
    editor.onOpen();
  };

  const openEdit = (tpl) => {
    setEditing(tpl);
    setForm({
      title: tpl.title || "",
      category: tpl.category || "",
      type: tpl.type || "",
      contents: tpl.contents || "",
      images: Array.isArray(tpl.images) ? tpl.images : [],
      published: tpl.published !== false,
    });
    editor.onOpen();
  };

  // Row-level publish toggle: single-field PATCH, no modal. Optimistic
  // UI with revert on error so the checkbox feels immediate.
  const togglePublished = async (tpl) => {
    const next = !(tpl.published !== false);
    setTogglingId(tpl.id);
    setTemplates((prev) =>
      prev.map((t) => (t.id === tpl.id ? { ...t, published: next } : t)),
    );
    try {
      await axios.patch(
        `${apiUrl}/admin/templates/${tpl.id}`,
        { published: next },
        { headers: authHeader() },
      );
    } catch (err) {
      console.error("Toggle publish failed", err);
      setTemplates((prev) =>
        prev.map((t) =>
          t.id === tpl.id ? { ...t, published: !next } : t,
        ),
      );
      toast({
        title: "Could not update publish state",
        description: err?.response?.data?.error || err.message,
        status: "error",
      });
    } finally {
      setTogglingId(null);
    }
  };

  const closeEditor = () => {
    if (saving) return;
    editor.onClose();
    setEditing(null);
  };

  const save = async () => {
    if (!form.title.trim() || !form.category.trim()) {
      toast({
        title: "Title and category are required",
        status: "warning",
      });
      return;
    }
    try {
      setSaving(true);
      if (editing) {
        await axios.patch(
          `${apiUrl}/admin/templates/${editing.id}`,
          form,
          { headers: authHeader() },
        );
        setTemplates((prev) =>
          prev.map((t) =>
            t.id === editing.id
              ? {
                  ...t,
                  ...form,
                  title: form.title.trim(),
                  category: form.category.trim(),
                  type: form.type || "",
                  images: form.images || [],
                  published: form.published !== false,
                }
              : t,
          ),
        );
        toast({ title: "Template updated", status: "success" });
      } else {
        const res = await axios.post(
          `${apiUrl}/admin/templates`,
          form,
          { headers: authHeader() },
        );
        setTemplates((prev) => [
          ...prev,
          {
            id: res.data.id,
            title: form.title.trim(),
            category: form.category.trim(),
            type: form.type || "",
            contents: form.contents,
            images: form.images || [],
            published: form.published !== false,
            updatedAt: new Date().toISOString(),
          },
        ]);
        toast({ title: "Template created", status: "success" });
      }
      closeEditor();
    } catch (err) {
      console.error("Save template failed", err);
      toast({
        title: "Save failed",
        description: err?.response?.data?.error || err.message,
        status: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (tpl) => {
    if (
      !window.confirm(
        `Delete template “${tpl.title}”? This cannot be undone.`,
      )
    ) {
      return;
    }
    try {
      setDeletingId(tpl.id);
      await axios.delete(`${apiUrl}/admin/templates/${tpl.id}`, {
        headers: authHeader(),
      });
      setTemplates((prev) => prev.filter((t) => t.id !== tpl.id));
      toast({ title: "Template deleted", status: "success" });
    } catch (err) {
      console.error("Delete template failed", err);
      toast({
        title: "Delete failed",
        description: err?.response?.data?.error || err.message,
        status: "error",
      });
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <HStack justify="center" py={10}>
        <Spinner />
        <Text>Loading templates…</Text>
      </HStack>
    );
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={4} gap={2}>
        <Text color="gray.600">
          Templates appear on the Templates tab of every Campaign Home
          for campaign and platform admins to copy.
        </Text>
        <Button
          leftIcon={<FaPlus />}
          variant="primary"
          size="sm"
          onClick={openCreate}
        >
          New Template
        </Button>
      </Flex>

      {sortedTemplates.length === 0 ? (
        <Text color="gray.500" py={4}>
          No templates yet. Create the first one.
        </Text>
      ) : (
        <Box overflowX="auto">
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th width="1%">Published</Th>
                <Th>Type</Th>
                <Th>Category</Th>
                <Th>Title</Th>
                <Th>Preview</Th>
                <Th width="1%">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {sortedTemplates.map((tpl) => {
                const tt = resolveTemplateType(tpl.type);
                return (
                <Tr key={tpl.id}>
                  <Td textAlign="center">
                    <Checkbox
                      isChecked={tpl.published !== false}
                      isDisabled={togglingId === tpl.id}
                      onChange={() => togglePublished(tpl)}
                      colorScheme="green"
                      aria-label={
                        tpl.published !== false
                          ? "Unpublish template"
                          : "Publish template"
                      }
                    />
                  </Td>
                  <Td whiteSpace="nowrap">
                    <HStack spacing={2} color="gray.700">
                      <Icon as={tt.icon} />
                      <Text fontSize="sm">{tt.label}</Text>
                    </HStack>
                  </Td>
                  <Td whiteSpace="nowrap">{tpl.category}</Td>
                  <Td>{tpl.title}</Td>
                  <Td>
                    <Text
                      noOfLines={1}
                      color="gray.600"
                      fontSize="sm"
                      maxW="360px"
                    >
                      {tpl.contents}
                    </Text>
                  </Td>
                  <Td whiteSpace="nowrap">
                    <HStack spacing={1}>
                      <Button
                        size="xs"
                        variant="outline"
                        leftIcon={<FaEdit />}
                        onClick={() => openEdit(tpl)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="xs"
                        variant="outline"
                        colorScheme="red"
                        leftIcon={<FaTrash />}
                        isLoading={deletingId === tpl.id}
                        onClick={() => remove(tpl)}
                      >
                        Delete
                      </Button>
                    </HStack>
                  </Td>
                </Tr>
              );
              })}
            </Tbody>
          </Table>
        </Box>
      )}

      <Modal isOpen={editor.isOpen} onClose={closeEditor} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editing ? "Edit Template" : "New Template"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={3} isRequired>
              <FormLabel>Title</FormLabel>
              <Input
                value={form.title}
                onChange={(e) =>
                  setForm((f) => ({ ...f, title: e.target.value }))
                }
                placeholder="e.g. Launch announcement email"
              />
            </FormControl>
            <FormControl mb={3} isRequired>
              <FormLabel>Category</FormLabel>
              <Input
                value={form.category}
                onChange={(e) =>
                  setForm((f) => ({ ...f, category: e.target.value }))
                }
                placeholder="e.g. Email, Facebook, Instagram"
              />
            </FormControl>
            <FormControl mb={3}>
              <FormLabel>Type</FormLabel>
              <Select
                placeholder="Choose a type"
                value={form.type}
                onChange={(e) =>
                  setForm((f) => ({ ...f, type: e.target.value }))
                }
              >
                {TEMPLATE_TYPES.map((t) => (
                  <option key={t.key} value={t.key}>
                    {t.label}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl mb={3}>
              <Checkbox
                isChecked={form.published !== false}
                onChange={(e) =>
                  setForm((f) => ({ ...f, published: e.target.checked }))
                }
                colorScheme="green"
              >
                Published (visible to campaign admins)
              </Checkbox>
            </FormControl>
            <FormControl>
              <FormLabel>Contents (Markdown)</FormLabel>
              <Tabs variant="enclosed" size="sm">
                <TabList>
                  <Tab>Edit</Tab>
                  <Tab>Preview</Tab>
                </TabList>
                <TabPanels>
                  <TabPanel px={0} pt={3}>
                    <Textarea
                      value={form.contents}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, contents: e.target.value }))
                      }
                      placeholder="Write your template in Markdown…"
                      rows={14}
                      fontFamily="mono"
                    />
                  </TabPanel>
                  <TabPanel px={0} pt={3}>
                    {form.contents.trim() ? (
                      <Box
                        borderWidth="1px"
                        borderRadius="md"
                        p={3}
                        bg="white"
                        minH="240px"
                        sx={markdownSx}
                      >
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {form.contents}
                        </ReactMarkdown>
                      </Box>
                    ) : (
                      <Text color="gray.500" py={4}>
                        Nothing to preview yet — write some markdown in the
                        Edit tab.
                      </Text>
                    )}
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </FormControl>

            <FormControl mt={4}>
              <HStack justify="space-between" align="center" mb={2}>
                <FormLabel mb={0}>Attachments</FormLabel>
                <Button
                  size="xs"
                  variant="outline"
                  leftIcon={<FaImage />}
                  onClick={onPickImage}
                  isLoading={uploadingImage}
                >
                  Add Image
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp"
                  style={{ display: "none" }}
                  onChange={onImageChosen}
                />
              </HStack>
              {(form.images || []).length === 0 ? (
                <Text color="gray.500" fontSize="sm" py={2}>
                  No images attached. Users see attachments below the message
                  body — good for social-media graphics they can download.
                </Text>
              ) : (
                <HStack spacing={3} flexWrap="wrap" align="flex-start">
                  {form.images.map((img, idx) => (
                    <Box
                      key={`${img.url}-${idx}`}
                      borderWidth="1px"
                      borderRadius="md"
                      p={2}
                      w="140px"
                    >
                      <Box
                        as="img"
                        src={img.url}
                        alt={img.filename}
                        maxW="100%"
                        h="100px"
                        objectFit="cover"
                        borderRadius="sm"
                        mb={1}
                      />
                      <Text fontSize="xs" noOfLines={1} title={img.filename}>
                        {img.filename}
                      </Text>
                      <Button
                        mt={1}
                        size="xs"
                        variant="outline"
                        colorScheme="red"
                        leftIcon={<FaTrash />}
                        width="100%"
                        onClick={() => removeImage(idx)}
                      >
                        Remove
                      </Button>
                    </Box>
                  ))}
                </HStack>
              )}
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={closeEditor}>
              Cancel
            </Button>
            <Button variant="primary" onClick={save} isLoading={saving}>
              {editing ? "Save" : "Create"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default TemplatesAdmin;
